import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    CircularProgress,
    IconButton,
    Divider
} from '@mui/material';
import {
    Close as CloseIcon,
    Download as DownloadIcon,
    Share as ShareIcon
} from '@mui/icons-material';
import { Document, Packer, Paragraph, HeadingLevel, TextRun } from 'docx';
import { toast } from 'react-toastify';
import { functions } from '../firebase';
import { httpsCallable } from 'firebase/functions';
import ReactMarkdown from 'react-markdown';
import ShareModal from './ShareModal';
import CreateAsanaTaskModal from './CreateAsanaTaskModal';

const ChatSummaryModal = ({ open, onClose, reportData, isLoading, user, asanaContext, clientId }) => {
    const [isShareModalOpen, setIsShareModalOpen] = useState(false);
    const [isAsanaModalOpen, setIsAsanaModalOpen] = useState(false);

    // --- Word Export Helpers ---

    const parseBoldText = (text) => {
        const children = [];
        const parts = text.split(/(\*\*.*?\*\*)/g);
        for (const part of parts) {
            if (part.startsWith('**') && part.endsWith('**')) {
                children.push(new TextRun({ text: part.slice(2, -2), bold: true }));
            } else if (part) {
                children.push(new TextRun({ text: part }));
            }
        }
        return children;
    };

    const parseReportTextToDocx = (text) => {
        const paragraphs = [];
        const lines = text.split('\n');

        for (const line of lines) {
            const trimmed = line.trim();
            if (!trimmed) continue;

            if (trimmed.startsWith('#### ')) {
                paragraphs.push(new Paragraph({
                    children: parseBoldText(trimmed.substring(5)),
                    heading: HeadingLevel.HEADING_4,
                    spacing: { before: 180, after: 80 }
                }));
            } else if (trimmed.startsWith('### ')) {
                paragraphs.push(new Paragraph({
                    children: parseBoldText(trimmed.substring(4)),
                    heading: HeadingLevel.HEADING_3,
                    spacing: { before: 200, after: 100 }
                }));
            } else if (trimmed.startsWith('## ')) {
                paragraphs.push(new Paragraph({
                    children: parseBoldText(trimmed.substring(3)),
                    heading: HeadingLevel.HEADING_2,
                    spacing: { before: 250, after: 120 }
                }));
            } else if (trimmed.startsWith('# ')) {
                paragraphs.push(new Paragraph({
                    children: parseBoldText(trimmed.substring(2)),
                    heading: HeadingLevel.HEADING_1,
                    spacing: { before: 300, after: 150 }
                }));
            } else if (trimmed.startsWith('- ') || trimmed.startsWith('• ')) {
                const bulletText = trimmed.substring(2);
                paragraphs.push(new Paragraph({
                    children: [new TextRun({ text: '• ' }), ...parseBoldText(bulletText)],
                    spacing: { after: 50 },
                    indent: { left: 360 }
                }));
            } else if (trimmed === '---') {
                paragraphs.push(new Paragraph({
                    text: '________________________________',
                    spacing: { before: 200, after: 200 }
                }));
            } else {
                paragraphs.push(new Paragraph({
                    children: parseBoldText(trimmed),
                    spacing: { after: 100 }
                }));
            }
        }

        return paragraphs;
    };

    // --- Export Handler ---

    const handleExportToWord = async () => {
        if (!reportData) {
            toast.error('No report data to export.');
            return;
        }
        try {
            const { metadata, reportText } = reportData;
            const doc = new Document({
                sections: [{
                    children: [
                        new Paragraph({ text: "Chat Summary", heading: HeadingLevel.TITLE }),
                        new Paragraph({ text: `Client: ${metadata.clientName}`, spacing: { after: 200 } }),
                        new Paragraph({ text: `Period: ${metadata.dateRange.from} to ${metadata.dateRange.to}`, spacing: { after: 400 } }),
                        ...parseReportTextToDocx(reportText),
                    ],
                }],
            });

            const blob = await Packer.toBlob(doc);
            const filename = `Chat_Summary_${metadata.clientName}_${metadata.dateRange.from}_to_${metadata.dateRange.to}.docx`;
            const link = document.createElement('a');
            const url = window.URL.createObjectURL(blob);
            link.href = url;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
            toast.success('Report exported successfully!');
        } catch (error) {
            console.error("Word export failed:", error);
            toast.error("Failed to export Word document.");
        }
    };

    // --- Share Handler ---

    const handleShare = async (sharePayload) => {
        const payloadForBackend = {
            recipientEmail: sharePayload.recipientEmail,
            personalMessage: sharePayload.personalMessage,
            reportData: reportData
        };

        try {
            const shareAmsReport = httpsCallable(functions, 'shareAmsReportByEmail');
            await shareAmsReport(payloadForBackend);
            toast.success("Chat Summary Report shared successfully!");
            return { success: true };
        } catch (error) {
            console.error("Chat Summary Share failed:", error);
            toast.error(`Failed to share report: ${error.message}`);
            return { success: false, error: error.message };
        }
    };

    return (
        <>
            <Dialog
                open={open}
                onClose={onClose}
                maxWidth="md"
                fullWidth
                PaperProps={{
                    sx: { minHeight: '70vh' }
                }}
            >
                <DialogTitle>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Typography variant="h6" fontWeight={600}>
                            {isLoading
                                ? 'Generating Chat Summary...'
                                : reportData
                                    ? `Chat Summary: ${reportData.metadata?.clientName || ''}`
                                    : 'Chat Summary'
                            }
                        </Typography>
                        <IconButton onClick={onClose} size="small">
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </DialogTitle>

                <DialogContent dividers>
                    {isLoading ? (
                        <Box
                            display="flex"
                            flexDirection="column"
                            alignItems="center"
                            justifyContent="center"
                            minHeight="300px"
                        >
                            <CircularProgress size={48} sx={{ mb: 2 }} />
                            <Typography variant="body1" color="text.secondary">
                                Generating your report...
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                This may take 2-3 minutes depending on the date range.
                            </Typography>
                        </Box>
                    ) : reportData ? (
                        <Box>
                            {/* Date range header */}
                            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
                                {reportData.metadata?.clientName} — {reportData.metadata?.dateRange?.from} to {reportData.metadata?.dateRange?.to}
                            </Typography>

                            {/* Rendered markdown content */}
                            <Box
                                sx={{
                                    '& h1': { fontSize: '1.5rem', fontWeight: 700, mt: 3, mb: 1.5 },
                                    '& h2': { fontSize: '1.25rem', fontWeight: 600, mt: 3, mb: 1, color: 'text.primary' },
                                    '& h3': { fontSize: '1.1rem', fontWeight: 600, mt: 2.5, mb: 0.75 },
                                    '& h4': { fontSize: '1rem', fontWeight: 600, mt: 2, mb: 0.5 },
                                    '& p': { mb: 1.5, lineHeight: 1.6 },
                                    '& ul, & ol': { pl: 3, mb: 1.5 },
                                    '& li': { mb: 0.5 },
                                    '& hr': { my: 2.5, border: 'none', borderTop: '1px solid', borderColor: 'grey.300' },
                                    '& strong': { fontWeight: 600 },
                                    '& a': { color: 'primary.main', textDecoration: 'underline' },
                                }}
                            >
                                <ReactMarkdown>{reportData.reportText}</ReactMarkdown>
                            </Box>

                            <Divider sx={{ mt: 3, mb: 1 }} />

                            <Typography variant="caption" color="text.secondary">
                                Generated: {new Date().toLocaleString()}
                            </Typography>
                        </Box>
                    ) : (
                        <Box
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                            minHeight="200px"
                        >
                            <Typography color="text.secondary">
                                No report data available.
                            </Typography>
                        </Box>
                    )}
                </DialogContent>

                <DialogActions sx={{ px: 3, py: 2 }}>
                    <Button onClick={onClose} color="inherit">
                        Close
                    </Button>
                    {reportData && !isLoading && (
                        <>
                            <Button
                                onClick={() => setIsShareModalOpen(true)}
                                startIcon={<ShareIcon />}
                            >
                                Share
                            </Button>
                            {!!asanaContext && (
                                <Button
                                    onClick={() => setIsAsanaModalOpen(true)}
                                >
                                    Task
                                </Button>
                            )}
                            <Button
                                onClick={handleExportToWord}
                                variant="contained"
                                startIcon={<DownloadIcon />}
                            >
                                Export to Word
                            </Button>
                        </>
                    )}
                </DialogActions>
            </Dialog>

            {/* Share Modal */}
            {isShareModalOpen && reportData && (
                <ShareModal
                    open={isShareModalOpen}
                    onClose={() => setIsShareModalOpen(false)}
                    item={reportData}
                    itemType="ams_report"
                    onShare={handleShare}
                    user={user}
                />
            )}

            {/* Asana Task Modal */}
            {isAsanaModalOpen && reportData && (
                <CreateAsanaTaskModal
                    open={isAsanaModalOpen}
                    onClose={() => setIsAsanaModalOpen(false)}
                    item={{
                        title: `Review Chat Summary for ${reportData.metadata.clientName}`,
                        content: reportData.reportText,
                        dataType: 'ams_report',
                        clientId: clientId,
                        asanaContext: asanaContext
                    }}
                    connectionContext={asanaContext}
                />
            )}
        </>
    );
};

export default ChatSummaryModal;